import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SessionUser } from '@katsquare/types';
import { Button, Card, ErrorText, Field, Input, Spinner } from '../components/ui';
import { ApiRequestError, forgotPassword, resetPassword, verifyEmail } from '../lib/api';
import { SESSION_KEY } from '../features/auth/useSession';

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center">
          <span className="text-lg font-semibold tracking-tight text-neutral-900">
            KatSquare Draft
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Confirms an email from its token link, then signs the user in. */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ran = useRef(false);

  const verify = useMutation<SessionUser, ApiRequestError>({
    mutationFn: () => verifyEmail(token),
    onSuccess: (user) => {
      queryClient.setQueryData(SESSION_KEY, user);
      navigate('/app', { replace: true });
    },
  });

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (token) verify.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <Shell>
      <Card className="p-6 text-center">
        {!token || verify.isError ? (
          <>
            <h1 className="mb-1 text-lg font-semibold text-neutral-900">Link expired</h1>
            <p className="mb-5 text-sm text-neutral-500">
              {verify.error?.message ?? 'This verification link is invalid or has expired.'}
            </p>
            <Link
              to="/login"
              className="block rounded-md bg-neutral-900 px-4 py-2 text-center text-sm font-medium text-white hover:bg-neutral-700"
            >
              Back to sign in
            </Link>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 py-4">
            <Spinner />
            <p className="text-sm text-neutral-500">Verifying your email…</p>
          </div>
        )}
      </Card>
    </Shell>
  );
}

/** Request a password-reset link by email. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const request = useMutation({ mutationFn: () => forgotPassword(email) });

  return (
    <Shell>
      <Card className="p-6">
        <h1 className="mb-1 text-lg font-semibold text-neutral-900">Reset your password</h1>
        {request.isSuccess ? (
          <p className="mt-2 text-sm text-neutral-500">
            If an account exists for <strong>{email}</strong>, we’ve sent a reset link. Check your inbox.
          </p>
        ) : (
          <>
            <p className="mb-5 text-sm text-neutral-500">
              Enter your email and we’ll send you a link to set a new password.
            </p>
            <form
              className="flex flex-col gap-4"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                request.mutate();
              }}
            >
              <Field label="Email">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </Field>
              <Button type="submit" disabled={request.isPending}>
                {request.isPending ? <Spinner /> : 'Send reset link'}
              </Button>
            </form>
          </>
        )}
        <p className="mt-4 text-center text-sm text-neutral-500">
          <Link className="font-medium text-neutral-900 underline" to="/login">
            Back to sign in
          </Link>
        </p>
      </Card>
    </Shell>
  );
}

/** Set a new password from a reset-token link. */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');

  const reset = useMutation<SessionUser, ApiRequestError>({
    mutationFn: () => resetPassword(token, password),
    onSuccess: (user) => {
      queryClient.setQueryData(SESSION_KEY, user);
      navigate('/app', { replace: true });
    },
  });

  return (
    <Shell>
      <Card className="p-6">
        <h1 className="mb-1 text-lg font-semibold text-neutral-900">Set a new password</h1>
        {!token ? (
          <p className="mt-2 text-sm text-red-500">This reset link is missing its token.</p>
        ) : (
          <>
            <p className="mb-5 text-sm text-neutral-500">
              Choose a new password for your account.
            </p>
            <form
              className="flex flex-col gap-4"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                reset.mutate();
              }}
            >
              <Field label="New password">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={10}
                  required
                />
              </Field>
              {reset.isError && <ErrorText>{reset.error.message}</ErrorText>}
              <Button type="submit" disabled={reset.isPending}>
                {reset.isPending ? <Spinner /> : 'Reset password'}
              </Button>
            </form>
          </>
        )}
        <p className="mt-4 text-center text-sm text-neutral-500">
          <Link className="font-medium text-neutral-900 underline" to="/login">
            Back to sign in
          </Link>
        </p>
      </Card>
    </Shell>
  );
}
